import { SelectDesignTokens } from '@primeuix/themes/types/select';

import { createDropdownOverlayTokens, createInputTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Select Component Configuration
 */
export function createSelect(s: SemanticTokens): SelectDesignTokens {
  const inputTokens = createInputTokens(s);
  const dropdownOverlayTokens = createDropdownOverlayTokens(s);
  return {
    root: {
      background: inputTokens.bg,
      disabledBackground: inputTokens.bgDisabled,
      filledBackground: inputTokens.bg,
      filledHoverBackground: inputTokens.bgHover,
      filledFocusBackground: inputTokens.bg,
      borderColor: inputTokens.border,
      hoverBorderColor: inputTokens.border,
      focusBorderColor: inputTokens.borderFocus,
      invalidBorderColor: inputTokens.borderInvalid,
      color: inputTokens.fg,
      disabledColor: inputTokens.fgDisabled,
      placeholderColor: inputTokens.placeholder,
      invalidPlaceholderColor: inputTokens.placeholder,
      shadow: 'none',
      paddingX: inputTokens.base.paddingX,
      paddingY: inputTokens.base.paddingY,
      borderRadius: inputTokens.base.radius,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
      transitionDuration: dropdownOverlayTokens.transition,
    },
    dropdown: {
      width: dropdownOverlayTokens.dropdown.size,
      color: dropdownOverlayTokens.dropdownIconFg,
    },
    overlay: {
      background: dropdownOverlayTokens.bg,
      borderColor: dropdownOverlayTokens.border,
      borderRadius: dropdownOverlayTokens.radius,
      color: dropdownOverlayTokens.fg,
      shadow: dropdownOverlayTokens.shadow,
    },
    list: {
      header: { padding: dropdownOverlayTokens.headerPadding },
      padding: dropdownOverlayTokens.listPadding,
      gap: dropdownOverlayTokens.listGap,
    },
    option: {
      focusBackground: dropdownOverlayTokens.option.bgFocus,
      selectedBackground: dropdownOverlayTokens.option.bgSelected,
      selectedFocusBackground: dropdownOverlayTokens.option.bgSelected,
      color: dropdownOverlayTokens.option.fg,
      focusColor: dropdownOverlayTokens.option.fgFocus,
      selectedColor: dropdownOverlayTokens.option.fgSelected,
      selectedFocusColor: dropdownOverlayTokens.option.fgSelected,
      padding: `${dropdownOverlayTokens.option.padding} ${dropdownOverlayTokens.option.padding}`,
      borderRadius: dropdownOverlayTokens.option.radius,
    },
    optionGroup: {
      background: 'transparent',
      color: dropdownOverlayTokens.optionGroup.fg,
      fontWeight: dropdownOverlayTokens.optionGroup.fontWeight,
      padding: `${dropdownOverlayTokens.option.padding} ${dropdownOverlayTokens.option.padding}`,
    },
    clearIcon: { color: dropdownOverlayTokens.clearIconFg },
    checkmark: { color: dropdownOverlayTokens.checkmarkFg },
    emptyMessage: {
      padding: `${dropdownOverlayTokens.emptyMessagePadding} ${dropdownOverlayTokens.emptyMessagePadding}`,
    },
    colorScheme: {
      light: {
        root: {
          background: inputTokens.bg,
          disabledBackground: inputTokens.bgDisabled,
          filledBackground: inputTokens.bg,
          filledHoverBackground: inputTokens.bgHover,
          filledFocusBackground: inputTokens.bg,
          borderColor: inputTokens.border,
          hoverBorderColor: inputTokens.border,
          focusBorderColor: inputTokens.borderFocus,
          invalidBorderColor: inputTokens.borderInvalid,
          color: inputTokens.fg,
          disabledColor: inputTokens.fgDisabled,
          placeholderColor: inputTokens.placeholder,
          invalidPlaceholderColor: inputTokens.placeholder,
        },
        dropdown: { color: dropdownOverlayTokens.dropdownIconFg },
        overlay: {
          background: dropdownOverlayTokens.bg,
          borderColor: dropdownOverlayTokens.border,
          color: dropdownOverlayTokens.fg,
        },
        option: {
          focusBackground: dropdownOverlayTokens.option.bgFocus,
          selectedBackground: dropdownOverlayTokens.option.bgSelected,
          selectedFocusBackground: dropdownOverlayTokens.option.bgSelected,
          color: dropdownOverlayTokens.option.fg,
          focusColor: dropdownOverlayTokens.option.fgFocus,
          selectedColor: dropdownOverlayTokens.option.fgSelected,
          selectedFocusColor: dropdownOverlayTokens.option.fgSelected,
        },
      },
    },
    css: () => `
        .p-select .p-select-dropdown {
          padding-right: ${dropdownOverlayTokens.triggerPaddingRight};
        }

        /* Keep the clear (×) icon clear of the dropdown chevron so they don't overlap. The clear
           icon is a static flex item (inset-* has no effect), and the enlarged chevron overflows its
           padded trigger box to the left, so separate them with a real margin. */
        .p-select .p-select-clear-icon {
          margin-inline-end: 1rem;
        }

        /* Chevron icon size - 1.5rem (24px) */
        .p-select .p-select-dropdown .p-icon,
        .p-select .p-select-dropdown svg {
          width: ${dropdownOverlayTokens.chevron.size};
          height: ${dropdownOverlayTokens.chevron.size};
        }
        
        /* Filter/search input */
        .p-select-filter {
          height: ${dropdownOverlayTokens.filter.height};
          font-size: ${dropdownOverlayTokens.filter.fontSize};
        }
        
        /* Filter search icon */
        .p-select-header .p-inputicon {
          font-size: ${dropdownOverlayTokens.filter.iconSize};
          top: 25%;
        }

        /* Disabled chevron icon color */
        .p-select.p-disabled .p-select-dropdown {
          color: ${inputTokens.fgDisabled};
        }

        .p-select-option.p-select-option-selected,
        .p-select-option.p-select-option-selected.p-focus {
          background: ${dropdownOverlayTokens.option.bgFocus};
          color: ${dropdownOverlayTokens.option.fg};
        }

        /* Truncate long option labels so the overlay doesn't stretch wider than its trigger */
        .p-select-option {
          min-width: 0;
        }
        .p-select-option-label,
        .p-select-option > * {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `,
  };
}
export const select = createSelect(SEMANTIC);
